import React, { useMemo } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box,
  Chip,
  Paper,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { formatPersonName } from '../utils/format';
import {
  WebsiteRequest,
  websiteRequestConfig,
} from '../utils/websiteRequest';

export type AppointmentBadge = {
  label: string;
  color?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
} | null;

export type LeadLike = {
  id: string;
  name: string;
  status?: string | null;
  source?: string | null;
  assignedTo?: string | null;
  nextFollowUpDate?: { toDate(): Date } | null;
  rating?: number | string | null;
  visitCount?: number | null;
  siteVisitCount?: number | null;
  visits?: number | string | null;
  visitNos?: number | string | null;
  enquiryType?: string | null;
  websiteRequest?: WebsiteRequest | null;
};

const ACCENT_PALETTE = [
  '#1e88e5', // blue
  '#00897b', // teal
  '#43a047', // green
  '#fb8c00', // orange
  '#8e24aa', // purple
  '#e53935', // red
];

function hashString(input: string) {
  let h = 0;
  for (let i = 0; i < input.length; i += 1) h = (h * 31 + input.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export default function LeadCompactCard(props: {
  lead: LeadLike;
  selected: boolean;
  onClick: () => void;
  onAddLogActivity?: () => void;
  statusLabel: string;
  assignedLabel: string;
  badge: AppointmentBadge;
  accentColor?: string | null;
  view?: 'card' | 'sheet';
  showMeta?: boolean;
  waUnreadCount?: number;
}) {
  const {
    lead,
    selected,
    onClick,
    onAddLogActivity,
    statusLabel,
    assignedLabel,
    badge,
    accentColor,
    view = 'card',
    showMeta = true,
    waUnreadCount = 0,
  } = props;
  const theme = useTheme();

  const accent = useMemo(() => {
    const c = typeof accentColor === 'string' ? accentColor.trim() : '';
    if (/^#([0-9a-fA-F]{6})$/.test(c)) return c;
    const key = lead.status || lead.source || lead.id;
    return ACCENT_PALETTE[hashString(String(key)) % ACCENT_PALETTE.length];
  }, [accentColor, lead.id, lead.source, lead.status]);

  const next = lead.nextFollowUpDate ? lead.nextFollowUpDate.toDate().toLocaleDateString() : '';
  const rating = Number(lead.rating || 0) || 0;
  const rawVisitCount = Number(
    lead.siteVisitCount ?? lead.visitCount ?? lead.visits ?? lead.visitNos ?? 0
  ) || 0;
  const statusNorm = String(lead.status || '').trim().toLowerCase();
  const siteVisitCount = rawVisitCount > 0
    ? rawVisitCount
    : (statusNorm === 'site visited' || statusNorm === 'site-visited' ? 1 : 0);
  const requestValue = lead.websiteRequest?.type || lead.websiteRequest?.label || lead.enquiryType;
  const requestConfig = requestValue ? websiteRequestConfig(requestValue) : null;
  const requestPending = Boolean(
    requestConfig &&
    String(lead.websiteRequest?.status || 'pending').toLowerCase() !== 'completed'
  );
  const requestChip = requestPending && requestConfig ? (
    <Chip
      label={requestConfig.shortLabel}
      size="small"
      color={requestConfig.color}
      variant="outlined"
      sx={{
        height: 18,
        borderRadius: 2,
        '& .MuiChip-label': { px: 0.75, fontWeight: 800, fontSize: 10.5 },
      }}
    />
  ) : null;

  // AppSheet-style row view (flat list, no per-item card).
  if (view === 'sheet') {
    return (
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') onClick();
        }}
        sx={{
          cursor: 'pointer',
          // "AppSheet" look: keep only a small left/right gutter.
          px: 1,
          py: 1.25,
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          backgroundColor: selected ? alpha(accent, 0.06) : 'transparent',
          transition: 'background-color 120ms ease',
          '&:hover': { backgroundColor: alpha(accent, 0.05) },
          ...(selected
            ? {
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: alpha(accent, 0.9),
                },
              }
            : null),
        }}
      >
        <Stack spacing={0.7}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, maxWidth: { xs: '64%', sm: 320 } }}>
              <Typography
                fontWeight={600}
                noWrap
                sx={{
                  fontSize: 16,
                  minWidth: 0,
                }}
              >
                {formatPersonName(lead.name)}
              </Typography>
              {waUnreadCount > 0 && (
                <Box
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.25,
                    bgcolor: '#25D366', color: '#fff',
                    borderRadius: 10, px: 0.6, py: 0.1,
                    fontSize: 10, fontWeight: 700, lineHeight: 1.6, flexShrink: 0,
                  }}
                >
                  <WhatsAppIcon sx={{ fontSize: 11 }} />
                  {waUnreadCount}
                </Box>
              )}
              {siteVisitCount > 0 && (
                <Chip
                  label={`Site Visited: ${siteVisitCount}`}
                  size="small"
                  sx={{
                    height: 18,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.grey[500], 0.12),
                    border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                    '& .MuiChip-label': { px: 0.75, fontWeight: 700, fontSize: 10.5, color: alpha('#000', 0.66) },
                  }}
                />
              )}
              {requestChip}
              {!!rating && <Rating value={rating} max={5} size="small" readOnly />}
            </Stack>
            <Box textAlign="right" sx={{ minWidth: 120 }}>
              {!!next && (
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: alpha(accent, 0.95) }}>
                  Followup: {next}
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', columnGap: 0.75, flexWrap: 'wrap' }}>
              {showMeta && (
                <Typography color="text.secondary" noWrap sx={{ fontSize: 13, minWidth: 0 }}>
                  {`${lead.source || '—'} • Assigned: ${assignedLabel}`}
                </Typography>
              )}
              {badge && (
                <Chip
                  icon={<CalendarTodayOutlinedIcon />}
                  label={badge.label}
                  size="small"
                  sx={{
                    height: 20,
                    borderRadius: 999,
                    ...(badge.color === 'success'
                      ? {
                          backgroundColor: alpha(theme.palette.success.main, 0.16),
                          border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                          '& .MuiChip-icon': { color: alpha(theme.palette.success.main, 0.95) },
                        }
                      : badge.color === 'warning'
                        ? {
                            backgroundColor: alpha(theme.palette.warning.main, 0.16),
                            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                            '& .MuiChip-icon': { color: alpha(theme.palette.warning.main, 0.95) },
                          }
                        : {
                            backgroundColor: alpha(accent, 0.12),
                            border: `1px solid ${alpha(accent, 0.28)}`,
                            '& .MuiChip-icon': { color: alpha(accent, 0.95) },
                          }),
                    '& .MuiChip-icon': { ml: '6px', fontSize: 12 },
                    '& .MuiChip-label': { px: 0.9, fontSize: 12, fontWeight: 700 },
                  }}
                />
              )}
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Chip
                label={statusLabel}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: 2,
                  backgroundColor: alpha(accent, 0.18),
                  border: `1px solid ${alpha(accent, 0.35)}`,
                  '& .MuiChip-label': { px: 0.9, fontWeight: 700, fontSize: 12, color: alpha('#000', 0.78) },
                }}
              />
              {!!onAddLogActivity && (
                <Chip
                  label="+"
                  size="small"
                  variant="outlined"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddLogActivity();
                  }}
                  sx={{
                    height: 20,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.grey[500], 0.12),
                    borderColor: alpha(theme.palette.grey[500], 0.24),
                    color: alpha('#000', 0.66),
                    '& .MuiChip-label': { px: 0.85, fontWeight: 700, fontSize: 11.5 },
                  }}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    );
  }

  const basePaperSx = {
    p: 1.25,
    borderRadius: 2,
    cursor: 'pointer',
    borderColor: selected ? 'primary.main' : 'divider',
    backgroundColor: selected ? 'primary.lighter' : 'background.paper',
    transition: 'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
    '&:hover': { transform: 'translateY(-1px)' },
  } as const;

  // Variant C (chosen): colorful but clean "card header" look
  return (
    <Paper
      variant="outlined"
      sx={{
        ...basePaperSx,
        position: 'relative',
        borderColor: selected ? alpha(accent, 0.7) : 'divider',
        borderWidth: selected ? 2 : 1,
        backgroundImage: `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha('#ffffff', 0.0)} 55%)`,
        ...(selected
          ? {
              backgroundImage: `linear-gradient(135deg, ${alpha(accent, 0.22)} 0%, ${alpha('#ffffff', 0.0)} 60%)`,
              backgroundColor: alpha(accent, 0.06),
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -1,
                top: -1,
                bottom: -1,
                width: 6,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                backgroundColor: alpha(accent, 0.9),
              },
            }
          : null),
        '&:hover': { borderColor: alpha(accent, 0.85), transform: 'translateY(-1px)' },
      }}
      onClick={onClick}
    >
      {selected && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: 999,
            backgroundColor: alpha('#fff', 0.9),
            border: `1px solid ${alpha(accent, 0.35)}`,
            pointerEvents: 'none',
          }}
        >
          <CheckCircleRoundedIcon sx={{ fontSize: 14, color: alpha(accent, 0.95) }} />
        </Box>
      )}
      <Stack spacing={0.8}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, maxWidth: { xs: '64%', sm: 260 } }}>
            <Typography
              fontWeight={900}
              noWrap
              sx={{
                minWidth: 0,
              }}
            >
              {formatPersonName(lead.name)}
            </Typography>
            {waUnreadCount > 0 && (
              <Box
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.25,
                  bgcolor: '#25D366', color: '#fff',
                  borderRadius: 10, px: 0.6, py: 0.1,
                  fontSize: 10, fontWeight: 700, lineHeight: 1.6, flexShrink: 0,
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 11 }} />
                {waUnreadCount}
              </Box>
            )}
            {siteVisitCount > 0 && (
              <Chip
                label={`Site Visited: ${siteVisitCount}`}
                size="small"
                sx={{
                  height: 18,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.grey[500], 0.12),
                  border: `1px solid ${alpha(theme.palette.grey[500], 0.24)}`,
                  '& .MuiChip-label': { px: 0.8, fontWeight: 700, fontSize: 10.5, color: alpha('#000', 0.66) },
                }}
              />
            )}
            {requestChip}
            {!!rating && <Rating value={rating} max={5} size="small" readOnly />}
          </Stack>
          <Box textAlign="right" sx={{ minWidth: 120 }}>
            {!!next && (
              <Typography variant="body2" sx={{ fontWeight: 700, color: alpha(accent, 0.95) }}>
                Followup: {next}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', columnGap: 0.75, flexWrap: 'wrap' }}>
            {showMeta && (
              <Typography variant="body2" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                {`${lead.source || '—'} • Assigned: ${assignedLabel}`}
              </Typography>
            )}
            {badge && (
              <Chip
                icon={<CalendarTodayOutlinedIcon />}
                label={badge.label}
                size="small"
                sx={{
                  height: 22,
                  borderRadius: 999,
                  ...(badge.color === 'success'
                    ? {
                        backgroundColor: alpha(theme.palette.success.main, 0.16),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                        '& .MuiChip-icon': { color: alpha(theme.palette.success.main, 0.95) },
                      }
                    : badge.color === 'warning'
                      ? {
                          backgroundColor: alpha(theme.palette.warning.main, 0.16),
                          border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                          '& .MuiChip-icon': { color: alpha(theme.palette.warning.main, 0.95) },
                        }
                      : {
                          backgroundColor: alpha(accent, 0.12),
                          border: `1px solid ${alpha(accent, 0.28)}`,
                          '& .MuiChip-icon': { color: alpha(accent, 0.95) },
                        }),
                  '& .MuiChip-icon': { ml: '6px', fontSize: 12 },
                  '& .MuiChip-label': { px: 1 },
                }}
              />
            )}
          </Box>
          <Stack direction="row" alignItems="center" spacing={0.6}>
            <Chip
              label={statusLabel}
              size="small"
              sx={{
                height: 22,
                borderRadius: 2,
                backgroundColor: alpha(accent, 0.18),
                border: `1px solid ${alpha(accent, 0.35)}`,
                '& .MuiChip-label': { px: 1, fontWeight: 700, color: alpha('#000', 0.78) },
              }}
            />
            {!!onAddLogActivity && (
              <Chip
                label="+"
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddLogActivity();
                }}
                sx={{
                  height: 22,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.grey[500], 0.12),
                  borderColor: alpha(theme.palette.grey[500], 0.24),
                  color: alpha('#000', 0.66),
                  '& .MuiChip-label': { px: 1, fontWeight: 700, fontSize: 12 },
                }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
